import { differenceInSeconds } from 'date-fns/differenceInSeconds'
import { createStore, sample } from 'effector'
import _ from 'lodash'
import { once } from 'patronum'

import { appParams, getRegisterInfo } from '@portal/client-info'

import { $portalProfile } from '@/entities/profile/portal'
import { analytics } from '@/shared/lib/analytics'
import { type PageHitPayload, PageHitPayloadSchema } from '@/shared/lib/analytics/events'
import { appStarted } from '@/shared/lib/app'
import { $lang } from '@/shared/lib/i18n'
import { persist } from '@/shared/lib/persist/local'
import { isNotNull } from '@/shared/lib/types'

const profileReceived = sample({
    clock: $portalProfile,
    filter: isNotNull,
})

// we store pagehit payload in localStorage to send it once when profile is received,
// even if user closes the tab without logging in and then opens it again by direct link
const $pageHitPayload = createStore<PageHitPayload | null>(null).reset(analytics.pagehit)

sample({
    clock: appStarted,
    source: $lang,
    filter: () =>
        // ref param should be present
        !_.isEmpty(appParams['ref']) &&
        // referrer should be present
        document.referrer !== '' &&
        // referrer should be external (not from our domain)
        !document.referrer.startsWith(window.location.origin) &&
        // referrer should not be from facebook iframe
        !document.referrer.startsWith('https://apps.facebook.com'),
    fn: (lang): PageHitPayload => ({
        hit_time: new Date(),
        register_info: getRegisterInfo(lang),
    }),
    target: $pageHitPayload,
})

persist({
    store: $pageHitPayload,
    key: 'analitics.pagehit.payload',
    serialize: JSON.stringify,
    deserialize: (value): PageHitPayload | null => {
        const parsedValue = JSON.parse(value)
        const parsedPayload = PageHitPayloadSchema.safeParse(parsedValue)
        if (!parsedPayload.success) {
            // invalid value or null
            return null
        }

        const hitLifeTime = differenceInSeconds(Date.now(), parsedPayload.data.hit_time)
        if (hitLifeTime >= 24 * 60 * 60) {
            // pagehit payload expires after 24 hours
            return null
        }

        return parsedPayload.data
    },
})

sample({
    // send pagehit event once when profile is received
    clock: once(profileReceived),
    source: $pageHitPayload,
    filter: isNotNull,
    target: analytics.pagehit,
})
